// ── Base config ──────────────────────────────────────────────
const BASE_URL =
  process.env.REACT_APP_API_URL || "http://localhost:5000/backend";

function getToken() {
  return localStorage.getItem("wqa_token");
}

// ── Mock data for demo mode (when backend is not available) ───
function isMockToken(token) {
  return token && token.startsWith("mock_token_");
}

const now = new Date();
const MOCK_RESPONSES = {
  "GET /auth/me": () => ({
    success: true,
    data: {
      id: 3,
      name: "Client User",
      email: "client@acmeplastics.com",
      user_type: "client",
    },
  }),
  "GET /weight-entries/client-dashboard": () => ({
    success: true,
    data: {
      myStats: { my_checks: 24, my_passed: 21, my_failed: 3 },
      liveTrend: Array.from({ length: 10 }, (_, i) => ({
        time: new Date(now - (9 - i) * 3 * 60000).toLocaleTimeString([], {
          hour: "2-digit",
          minute: "2-digit",
        }),
        weight: +(490 + Math.random() * 20 - 10).toFixed(1),
      })),
      recentEntries: [
        {
          product_name: "Bumper A",
          product_code: "BMP-A01",
          weight: 498.2,
          result: "Pass",
          recorded_at: new Date(now - 5 * 60000),
        },
        {
          product_name: "Panel B",
          product_code: "PNL-B02",
          weight: 512.1,
          result: "Fail",
          recorded_at: new Date(now - 12 * 60000),
        },
        {
          product_name: "Cover C",
          product_code: "CVR-C03",
          weight: 475.8,
          result: "Pass",
          recorded_at: new Date(now - 20 * 60000),
        },
        {
          product_name: "Bumper A",
          product_code: "BMP-A01",
          weight: 495.5,
          result: "Pass",
          recorded_at: new Date(now - 35 * 60000),
        },
        {
          product_name: "Panel B",
          product_code: "PNL-B02",
          weight: 505.0,
          result: "Pass",
          recorded_at: new Date(now - 50 * 60000),
        },
      ],
    },
  }),
  "GET /weight-entries": () => ({
    success: true,
    data: {
      entries: Array.from({ length: 20 }, (_, i) => ({
        id: i + 1,
        product_name: ["Bumper A", "Panel B", "Cover C", "Bracket D"][i % 4],
        product_code: ["BMP-A01", "PNL-B02", "CVR-C03", "BRK-D04"][i % 4],
        weight: +(490 + Math.random() * 30 - 15).toFixed(1),
        result: Math.random() > 0.15 ? "Pass" : "Fail",
        recorded_at: new Date(now - i * 15 * 60000),
      })),
    },
  }),
};

async function request(method, path, body = null) {
  const token = getToken();

  // ── Mock mode: if token is a demo token, return mock data ──
  if (isMockToken(token)) {
    // Strip query string for mock key lookup
    const basePath = path.split("?")[0];
    const key = `${method} ${basePath}`;
    const mockFn = MOCK_RESPONSES[key];
    if (mockFn) {
      // Simulate slight network delay for realism
      await new Promise((r) => setTimeout(r, 200));
      return mockFn();
    }
    // For unrecognised mock endpoints, return empty success
    return { success: true, data: {} };
  }

  // ── Real backend request ───────────────────────────────────
  const headers = { "Content-Type": "application/json" };
  if (token) headers["Authorization"] = `Bearer ${token}`;
  // ajay:code 11-08-26
  const activeCompany = localStorage.getItem("wqa_active_company");
  if (activeCompany) headers["x-active-company"] = activeCompany;

  const res = await fetch(`${BASE_URL}${path}`, {
    method,
    headers,
    body: body ? JSON.stringify(body) : undefined,
  });

  const data = await res.json();

  if (!res.ok) {
    const err = new Error(data.message || "API Error");
    err.status = res.status;
    err.data = data;
    throw err;
  }

  return data;
}

async function requestForm(method, path, formData) {
  const token = getToken();

  if (isMockToken(token)) {
    await new Promise((r) => setTimeout(r, 200));
    return { success: true, data: {}, message: "Submitted (demo mode)" };
  }

  const headers = {};
  if (token) headers["Authorization"] = `Bearer ${token}`;

  // ajay:code 11-08-26
  const activeCompany = localStorage.getItem("wqa_active_company");
  if (activeCompany) headers["x-active-company"] = activeCompany;

  const res = await fetch(`${BASE_URL}${path}`, {
    method,
    headers,
    body: formData,
  });
  const data = await res.json();

  if (!res.ok) {
    const err = new Error(data.message || "API Error");
    err.status = res.status;
    err.data = data;
    throw err;
  }
  return data;
}

const get = (path) => request("GET", path);
const post = (path, body) => request("POST", path, body);
const put = (path, body) => request("PUT", path, body);
const patch = (path, body) => request("PUT", path, body); //ajay:code 30-07-26
const del = (path) => request("DELETE", path);
const postForm = (path, formData) => requestForm("POST", path, formData);

// ── Auth ──────────────────────────────────────────────────────
export const authAPI = {
  login: (email, password) => post("/auth/login", { email, password }),
  me: () => get("/auth/me"),
};

// ── Companies (superadmin) ────────────────────────────────────
export const companyAPI = {
  stats: () => get("/companies/stats"),
  list: () => get("/companies"),
  get: (id) => get(`/companies/${id}`),
  create: (data) => post("/companies", data),
  update: (id, data) => put(`/companies/${id}`, data),
  delete: (id) => del(`/companies/${id}`),
  // ajay:code 22-08-26
  getMy: () => get("/companies/me"),
  uploadLogo: (file) => {
    const fd = new FormData();
    fd.append("logo", file);
    return postForm("/companies/me/logo", fd);
  },
};

// ── Users ─────────────────────────────────────────────────────
export const userAPI = {
  list: () => get("/users"),
  create: (data) => post("/users", data),
  update: (id, data) => put(`/users/${id}`, data),
  delete: (id) => del(`/users/${id}`),
};

// ── Products (Masters) ────────────────────────────────────────
export const productAPI = {
  list: () => get("/products"),
  get: (id) => get(`/products/${id}`),
  create: (data) => post("/products", data),
  update: (id, data) => put(`/products/${id}`, data),
  delete: (id) => del(`/products/${id}`),
  scan: (serial) => get(`/products/scan/${encodeURIComponent(serial)}`),
  uploadImage: (id, file) => {
    const fd = new FormData();
    fd.append("image", file);
    return postForm(`/products/${id}/image`, fd);
  },
};

// ── Roles ─────────────────────────────────────────────────────
export const roleAPI = {
  list: () => get("/roles"),
  create: (data) => post("/roles", data),
  update: (id, data) => put(`/roles/${id}`, data),
  delete: (id) => del(`/roles/${id}`),
};

// ── Weight Entries ────────────────────────────────────────────
export const weightAPI = {
  dashboard: (range = "today") =>
    get(`/weight-entries/dashboard?range=${range}`),
  clientDashboard: () => get("/weight-entries/client-dashboard"),
  trend: (range = "today") => get(`/weight-entries/trend?range=${range}`),
  list: (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return get(`/weight-entries${qs ? "?" + qs : ""}`);
  },
  create: (data) => post("/weight-entries", data),

  delete: (id) => del(`/weight-entries/${id}`), //30/07/2026 Diksha Changes
  // ajay:code 24-07-26
  spcData: (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return get(`/weight-entries/spc-data${qs ? "?" + qs : ""}`);
  },

  // NEW — product-wise SPC dashboard
  productDashboard: (
    productId,
    subgroupSize = 5,
    startDate = "",
    endDate = "",
  ) => {
    let url = `/weight-entries/product-dashboard/${productId}?subgroupSize=${subgroupSize}`;
    if (startDate) url += `&startDate=${startDate}`;
    if (endDate) url += `&endDate=${endDate}`;
    return get(url);
  },
};

// ── Clients (superadmin) ──────────────────────────────────────
export const clientAPI = {
  list: () => get("/clients"),
  create: (data) => post("/clients", data),
  update: (id, data) => put(`/clients/${id}`, data),
  delete: (id) => del(`/clients/${id}`),
};

// ── Client Users (superadmin) ─────────────────────────────────
// export const clientUserAPI = {
//   list: () => get("/client-users"),
//   create: (data) => post("/client-users", data),
//   toggleStatus: (id, status) => put(`/client-users/${id}/status`, { status }),
//   delete: (id) => del(`/client-users/${id}`),
//   update: (id, data) => put(`/client-users/${id}`, data),
// };

// ── User Forms (company admin) ─────────────────────────────
export const userFormAPI = {
  list: () => get("/user-forms"),
  getOne: (id) => get(`/user-forms/${id}`),
  create: (data) => post("/user-forms", data),
  update: (id, data) => put(`/user-forms/${id}`, data),
  toggleStatus: (id, status) => put(`/user-forms/${id}/status`, { status }),
  delete: (id) => del(`/user-forms/${id}`),
  // ── Client-facing: active forms to fill + submit ──────────
  active: () => get("/user-forms/active"),
  submit: (id, formData) => postForm(`/user-forms/${id}/submit`, formData),
  submissions: (id) => get(`/user-forms/${id}/submissions`),
};

// ── Dashboard / Analytics ─────────────────────────────────────
export const dashboardAPI = {
  overview: () => get("/dashboard"),
  // FIX: the analytics route is mounted under /weight-entries (see
  // server.js -> app.use('/api/weight-entries', require('./src/routes/weightEntries'))
  // and weightEntries.js -> router.get('/analytics', ...)). There is no
  // /api/dashboard/* route registered anywhere in server.js, so the old
  // path here was hitting a 404 and analytics/vendor/part charts stayed empty.
  analytics: (days, vendorId) =>
    get(
      `/weight-entries/analytics?days=${days}${vendorId ? `&vendor_id=${vendorId}` : ""}`,
    ),
};

// ajay:code 30-07-26
// ── PDIR (Pre-Despatch Inspection Report) ───────────────────────
export const pdirAPI = {
  listParameters: (productId) =>
    get(`/pdir/parameters?product_id=${productId}`),
  createParameter: (data) => post("/pdir/parameters", data),
  updateParameter: (id, data) => put(`/pdir/parameters/${id}`, data),
  // toggleParameter: (id) => patch(`/pdir/parameters/${id}/toggle`),
  toggleParameter: (id) => patch(`/pdir/parameters/${id}/toggle`, {}),

  listReports: (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return get(`/pdir/reports${qs ? "?" + qs : ""}`);
  },
  getReport: (id) => get(`/pdir/reports/${id}`),
  createReport: (data) => post("/pdir/reports", data),
  updateReport: (id, data) => put(`/pdir/reports/${id}`, data),
  // finalizeReport: (id) => patch(`/pdir/reports/${id}/finalize`),
  finalizeReport: (id) => patch(`/pdir/reports/${id}/finalize`, {}),
};

// FPIR ajay:code 03-08-26
// ── FPIR (First/Inprocess/Last Piece Inspection Report) ─────────
export const fpirAPI = {
  listParameters: (productId) =>
    get(`/fpir/parameters?product_id=${productId}`),
  createParameter: (data) => post("/fpir/parameters", data),
  updateParameter: (id, data) => put(`/fpir/parameters/${id}`, data),
  // toggleParameter: (id) => patch(`/fpir/parameters/${id}/toggle`),
  toggleParameter: (id) => patch(`/fpir/parameters/${id}/toggle`, {}),

  listReports: (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return get(`/fpir/reports${qs ? "?" + qs : ""}`);
  },
  getReport: (id) => get(`/fpir/reports/${id}`),
  createReport: (data) => post("/fpir/reports", data),
  updateReport: (id, data) => put(`/fpir/reports/${id}`, data),
  // finalizeReport: (id) => patch(`/fpir/reports/${id}/finalize`),
  finalizeDocument: (id) => patch(`/pfmea/${id}/finalize`, {}),
};

// ── PFMEA (Process Failure Mode & Effect Analysis) ajay:code 19-08-26 ───────────────
export const pfmeaAPI = {
  listDocuments: (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return get(`/pfmea${qs ? "?" + qs : ""}`);
  },
  getDocument: (id) => get(`/pfmea/${id}`),
  createDocument: (data) => post("/pfmea", data),
  updateDocument: (id, data) => put(`/pfmea/${id}`, data),
  finalizeDocument: (id) => patch(`/pfmea/${id}/finalize`),
};

// ── Control Plan ajay:code 27-08-26 ───────────────────────────────────
export const controlPlanAPI = {
  listDocuments: (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return get(`/control-plan${qs ? "?" + qs : ""}`);
  },
  getDocument: (id) => get(`/control-plan/${id}`),
  createDocument: (data) => post("/control-plan", data),
  updateDocument: (id, data) => put(`/control-plan/${id}`, data),
  finalizeDocument: (id) => patch(`/control-plan/${id}/finalize`),
};

// ajay:code 11-08-26
// ── Client Access (client_user role — company list + page-password verify) ──
export const clientAccessAPI = {
  myCompanies: () => get("/client-access/companies"),
  verifyAccess: (company_id, password) =>
    post("/client-access/verify", { company_id, password }),
};

// ajay:code 11-08-26
// ── Client Users (company-side — link/manage existing identities) ──────────
// export const clientUserAPI = {
//   listAvailable: () => get("/client-users/available"),
//   listLinked: () => get("/client-users"),
//   addToCompany: (data) => post("/client-users/link", data),
//   updateAccess: (linkId, data) => put(`/client-users/link/${linkId}`, data),
//   toggleLink: (linkId) => patch(`/client-users/link/${linkId}/toggle`),
// };
// ajay:code 12-08-26
export const clientUserAPI = {
  listLinked: () => get("/client-users"),
  updateAccess: (clientUserId, data) =>
    put(`/client-users/${clientUserId}/access`, data),
  // toggleLink: (clientUserId) => patch(`/client-users/${clientUserId}/toggle`),
  toggleLink: (clientUserId) =>
    patch(`/client-users/${clientUserId}/toggle`, {}),
};

// ajay:code 11-08-26
// ── Superadmin: Client User identities (company-independent) ───────────────
export const superadminClientUserAPI = {
  listAll: () => get("/superadmin/client-users"),
  create: (data) => post("/superadmin/client-users", data),
  update: (id, data) => put(`/superadmin/client-users/${id}`, data),
};
